import React, { useState, useEffect } from 'react'
import { Button, message } from 'antd'
import { CloseOutlined, DownloadOutlined, SafetyOutlined } from '@ant-design/icons'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const PWAInstallPrompt: React.FC = () => {
  const location = useLocation()
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isBannerVisible, setIsBannerVisible] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  
  // 只在首页显示
  const isHomePage = location.pathname === '/'

  useEffect(() => {
    // 检查是否已经安装
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isInWebAppiOS = (window.navigator as any).standalone === true
    if (isStandalone || isInWebAppiOS) {
      setIsInstalled(true)
      return
    }

    // 检测 iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
    setIsIOS(iOS)

    // 检测 Edge 浏览器
    const isEdge = /Edg/i.test(navigator.userAgent)
    const isDev = import.meta.env.DEV
    
    if (isDev) {
      console.log('[PWA横幅] 浏览器检测:', {
        userAgent: navigator.userAgent,
        isEdge,
        isIOS
      })
    }

    // 监听 beforeinstallprompt 事件（支持 Chrome、Edge、Opera 等基于 Chromium 的浏览器）
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      const promptEvent = e as BeforeInstallPromptEvent
      setDeferredPrompt(promptEvent)
      if (isDev) {
        console.log('[PWA横幅] beforeinstallprompt 事件触发，浏览器:', isEdge ? 'Edge' : 'Chrome/其他')
      }
    }

    // 监听安装完成事件
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setIsBannerVisible(false)
      message.success('应用已成功安装！')
    }

    // 添加事件监听器
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    // Edge 浏览器可能需要额外检测：检查是否支持 PWA 安装
    // 如果一段时间后还没有收到 beforeinstallprompt 事件，但满足 PWA 条件，也可以显示横幅
    // 注意：Edge 在某些情况下可能不会立即触发 beforeinstallprompt，但横幅仍然可以显示

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  // 监听路由变化和deferredPrompt，控制横幅显示
  useEffect(() => {
    const isDev = import.meta.env.DEV
    
    if (isDev) {
      console.log('[PWA横幅] 状态检查:', {
        isHomePage,
        isInstalled,
        deferredPrompt: !!deferredPrompt,
        isIOS,
        location: location.pathname
      })
    }

    if (!isHomePage) {
      setIsBannerVisible(false)
      return
    }

    if (isInstalled) {
      if (isDev) console.log('[PWA横幅] 已安装，不显示')
      setIsBannerVisible(false)
      return
    }

    const hasSeenPrompt = localStorage.getItem('pwa-install-prompt-seen')
    if (isDev) {
      console.log('[PWA横幅] 是否已看过提示:', hasSeenPrompt, '开发模式:', isDev)
    }
    
    // 🔥 开发环境：忽略localStorage标记，允许测试
    // 生产环境：如果已看过，不显示
    if (hasSeenPrompt && !isDev) {
      if (isDev) console.log('[PWA横幅] 用户已看过提示，不显示')
      setIsBannerVisible(false)
      return
    }

    // 检测是否支持 PWA 安装（Chrome、Edge、Opera 等基于 Chromium 的浏览器）
    const isChromiumBased = /Chrome|Edg|Opera/i.test(navigator.userAgent) && !/OPR/i.test(navigator.userAgent)
    const isEdge = /Edg/i.test(navigator.userAgent)
    
    // 如果有deferredPrompt或iOS，延迟显示
    // 对于 Edge/Chrome 等浏览器，即使暂时没有 deferredPrompt，如果满足 PWA 条件也可以显示横幅
    // 🔥 开发环境：即使没有deferredPrompt也显示（用于测试）
    const shouldShow = deferredPrompt || isIOS || (isChromiumBased && isHomePage) || (isDev && isHomePage)
    if (isDev) {
      console.log('[PWA横幅] 是否应该显示:', shouldShow, {
        deferredPrompt: !!deferredPrompt,
        isIOS,
        isChromiumBased,
        isEdge,
        isDev,
        isHomePage
      })
    }
    
    if (shouldShow) {
      // 开发环境：立即显示或缩短延迟；生产环境：延迟3秒
      const delay = isDev ? 100 : 3000
      if (isDev) console.log('[PWA横幅] 准备显示横幅，延迟', delay, 'ms')
      const timer = setTimeout(() => {
        if (isDev) console.log('[PWA横幅] ✅ 显示横幅')
        setIsBannerVisible(true)
      }, delay)
      
      return () => clearTimeout(timer)
    } else {
      if (isDev) console.log('[PWA横幅] ❌ 不满足显示条件')
    }
  }, [isHomePage, deferredPrompt, isIOS, isInstalled, location.pathname])

  const handleInstall = async () => {
    if (deferredPrompt) {
      // 显示浏览器安装提示
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        message.success('正在安装应用...')
      }
      
      setDeferredPrompt(null)
      setIsBannerVisible(false)
      localStorage.setItem('pwa-install-prompt-seen', 'true')
    }
  }

  const handleClose = () => {
    setIsBannerVisible(false)
    localStorage.setItem('pwa-install-prompt-seen', 'true')
  }

  // 🔥 开发环境：添加测试按钮，可以强制显示横幅
  const handleTestShow = () => {
    localStorage.removeItem('pwa-install-prompt-seen')
    setIsBannerVisible(true)
    if (import.meta.env.DEV) {
      console.log('[PWA横幅] 测试模式：强制显示横幅')
    }
  }

  // 如果已安装，不显示
  if (isInstalled) {
    return null
  }

  const isDev = import.meta.env.DEV
  
  // 如果不在首页，不显示（但开发环境可以显示测试按钮）
  if (!isHomePage) {
    // 开发环境：在任意页面显示测试按钮
    if (isDev) {
      return (
        <div className="fixed top-4 right-4 z-[200] bg-yellow-500 text-white px-3 py-2 rounded-lg shadow-lg cursor-pointer hover:bg-yellow-600 transition-colors">
          <button onClick={handleTestShow} className="text-sm font-semibold">
            测试PWA横幅
          </button>
        </div>
      )
    }
    return null
  }
  
  // 检测是否支持 PWA 安装的浏览器
  const isChromiumBased = /Chrome|Edg|Opera/i.test(navigator.userAgent) && !/OPR/i.test(navigator.userAgent)
  
  // 生产环境：需要deferredPrompt、iOS或支持PWA的Chromium浏览器才显示
  // 开发环境：允许显示用于测试
  if (!isDev && !deferredPrompt && !isIOS && !isChromiumBased) {
    // 生产环境：如果不支持PWA安装，不显示
    return null
  }

  // 开发环境：在首页显示测试按钮（如果横幅未显示）
  const showTestButton = isDev && !isBannerVisible

  return (
    <>
      {/* 开发环境测试按钮 */}
      {showTestButton && (
        <div className="fixed top-4 right-4 z-[200] bg-yellow-500 text-white px-3 py-2 rounded-lg shadow-lg cursor-pointer hover:bg-yellow-600 transition-colors">
          <button onClick={handleTestShow} className="text-sm font-semibold">
            测试PWA横幅
          </button>
        </div>
      )}
      
      <AnimatePresence>
        {isBannerVisible && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed left-0 right-0 z-[100] pwa-install-banner top-0"
          style={{
            // 横幅显示在顶部菜单（Header）的顶部
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.75) 0%, rgba(118, 75, 162, 0.75) 100%)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            '--pwa-banner-height': '56px' // 设置横幅高度变量
          } as React.CSSProperties}
          role="banner"
          aria-label="PWA安装提示"
        >
          <div className="max-w-7xl mx-auto" style={{ padding: `var(--spacing-sm) var(--spacing-md)` }}>
            <div className="flex items-center justify-between" style={{ gap: 'var(--spacing-lg)' }}>
              {/* 左侧内容 */}
              <div className="flex items-center flex-1 min-w-0" style={{ gap: 'var(--spacing-md)' }}>
                <div className="flex-shrink-0">
                  {/* 苹果风格的"添加到主屏幕"图标 - 带上下移动动画 */}
                  <motion.div
                    animate={{
                      y: [0, -4, 0],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <svg 
                      width="32" 
                      height="32" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      style={{ 
                        color: '#ffffff',
                        display: 'block'
                      }}
                    >
                      {/* 方框 */}
                      <rect 
                        x="4" 
                        y="4" 
                        width="16" 
                        height="16" 
                        rx="3" 
                        stroke="#ffffff" 
                        strokeWidth="2" 
                        fill="none"
                      />
                      {/* 向上的箭头 */}
                      <path 
                        d="M12 6 L12 18 M8 10 L12 6 L16 10" 
                        stroke="#ffffff" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </motion.div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm md:text-base" style={{ marginBottom: 'var(--spacing-xs)' }}>
                    安装AI到主屏幕/桌面
                  </div>
                  <div className="text-white/90 text-[11px] md:text-xs">
                    {isIOS ? (
                      '点击分享，选择"添加到主屏幕"'
                    ) : (
                      '支持电脑和手机端，轻松使用，92%的用户已安装'
                    )}
                  </div>
                </div>
              </div>

              {/* 右侧按钮 */}
              <div className="flex items-center flex-shrink-0" style={{ gap: 'var(--spacing-sm)' }}>
                {/* 如果有 deferredPrompt，显示安装按钮 */}
                {deferredPrompt ? (
                  <Button
                    type="primary"
                    size="small"
                    icon={<SafetyOutlined />}
                    onClick={handleInstall}
                    className="font-semibold"
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: '#ffffff',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 'var(--button-border-radius)',
                      height: '32px',
                      paddingLeft: 'var(--spacing-md)',
                      paddingRight: 'var(--spacing-md)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'
                    }}
                    aria-label="立即安装应用"
                  >
                    <span className="hidden sm:inline" style={{ color: '#ffffff' }}>立即安装</span>
                    <span className="sm:hidden" style={{ color: '#ffffff' }}>安装</span>
                  </Button>
                ) : (
                  // 对于 Edge/Chrome 等浏览器，即使没有 deferredPrompt，也显示安装按钮
                  // 点击后显示安装指引
                  (() => {
                    const isEdge = /Edg/i.test(navigator.userAgent)
                    const isChrome = /Chrome/i.test(navigator.userAgent) && !/Edg/i.test(navigator.userAgent)
                    const isChromiumBased = isEdge || isChrome
                    
                    if (isChromiumBased && !isIOS) {
                      return (
                        <Button
                          type="primary"
                          size="small"
                          icon={<SafetyOutlined />}
                          onClick={() => {
                            const edgeMessage = (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                请点击地址栏右侧的
                                <svg 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 16 16" 
                                  fill="none" 
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
                                >
                                  {/* Edge 风格的应用图标：方块，右上角有开口和箭头 */}
                                  {/* 主方块 */}
                                  <rect 
                                    x="2" 
                                    y="2" 
                                    width="10" 
                                    height="10" 
                                    rx="1" 
                                    stroke="currentColor" 
                                    strokeWidth="1.2" 
                                    fill="none"
                                  />
                                  {/* 右上角开口部分 */}
                                  <path 
                                    d="M 12 2 L 14 2 L 14 4" 
                                    stroke="currentColor" 
                                    strokeWidth="1.2" 
                                    strokeLinecap="round"
                                    fill="none"
                                  />
                                  {/* 朝向右上角的箭头（更简洁） */}
                                  <path 
                                    d="M 9.5 4.5 L 13.5 2.5 M 9.5 5.5 L 11.5 4.5" 
                                    stroke="currentColor" 
                                    strokeWidth="1.2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    fill="none"
                                  />
                                </svg>
                                <strong>应用</strong>图标，然后选择"安装此站点为应用"
                              </span>
                            )
                            message.info(
                              isEdge ? edgeMessage : '请点击地址栏右侧的"安装"图标，或使用菜单中的"安装"选项',
                              5
                            )
                          }}
                          className="font-semibold"
                          style={{
                            background: 'rgba(255, 255, 255, 0.2)',
                            color: '#ffffff',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: 'var(--button-border-radius)',
                            height: '32px',
                            paddingLeft: 'var(--spacing-md)',
                            paddingRight: 'var(--spacing-md)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'
                          }}
                          aria-label="查看安装指引"
                        >
                          <span className="hidden sm:inline" style={{ color: '#ffffff' }}>立即安装</span>
                          <span className="sm:hidden" style={{ color: '#ffffff' }}>安装</span>
                        </Button>
                      )
                    }
                    return null
                  })()
                )}
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={handleClose}
                  className="text-white hover:bg-white/20 border-none"
                  style={{
                    height: '32px',
                    width: '32px',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  aria-label="关闭横幅"
                />
              </div>
            </div>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default PWAInstallPrompt
